import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, ArrowUpDown, CalendarPlus, Pencil, Plus, Trash2 } from 'lucide-react';
import { PageBody, PageHeader } from '@/components/AppShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogBody, DialogFooter } from '@/components/ui/dialog';
import {
  chartApi,
  costCentersApi,
  partiesApi,
  recurringApi,
  type Direction,
  type RecurringRule,
  type RecurringStatus,
} from '@/api';
import { formatBRL } from '@/lib/utils';
import { ApiError } from '@/lib/api';

const statusVariant: Record<RecurringStatus, 'success' | 'warning' | 'default' | 'danger'> = {
  ACTIVE: 'success', WAITING: 'warning', PAUSED: 'default', CANCELED: 'danger',
};
const statusLabel: Record<RecurringStatus, string> = {
  ACTIVE: 'Ativo', WAITING: 'Aguardando', PAUSED: 'Pausado', CANCELED: 'Cancelado',
};

// Ordem usada para ordenar a coluna "Status" — ativos primeiro, cancelados por último.
const statusOrder: Record<RecurringStatus, number> = {
  ACTIVE: 0, WAITING: 1, PAUSED: 2, CANCELED: 3,
};

type SortColumn =
  | 'party'
  | 'description'
  | 'chart'
  | 'dayOfMonth'
  | 'amount'
  | 'status'
  | 'lastGeneratedAt';

type SortDir = 'asc' | 'desc';

interface SortState {
  column: SortColumn;
  dir: SortDir;
}

function compareRules(a: RecurringRule, b: RecurringRule, column: SortColumn): number {
  switch (column) {
    case 'party':       return a.party.name.localeCompare(b.party.name, 'pt-BR');
    case 'description': return a.description.localeCompare(b.description, 'pt-BR');
    case 'chart':       return a.chartOfAccount.name.localeCompare(b.chartOfAccount.name, 'pt-BR');
    case 'dayOfMonth':  return a.dayOfMonth - b.dayOfMonth;
    case 'amount':      return Number(a.amount) - Number(b.amount);
    case 'status':      return statusOrder[a.status] - statusOrder[b.status];
    case 'lastGeneratedAt': {
      const av = a.lastGeneratedAt ? new Date(a.lastGeneratedAt).getTime() : 0;
      const bv = b.lastGeneratedAt ? new Date(b.lastGeneratedAt).getTime() : 0;
      return av - bv;
    }
  }
}

export function RecorrentesPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<Direction>('IN');
  const [sort, setSort] = useState<SortState>({ column: 'status', dir: 'asc' });
  const [formDialog, setFormDialog] = useState<
    | { mode: 'create'; direction: Direction }
    | { mode: 'edit'; rule: RecurringRule }
    | null
  >(null);

  // Busca TODAS as regras (ambas as direções) — usadas tanto para os cards de total
  // quanto para a tabela filtrada pela aba ativa.
  const { data: allRules, isLoading } = useQuery({
    queryKey: ['recurring', 'all'],
    queryFn: () => recurringApi.list(),
  });

  // Total mensal das regras ATIVAS por direção.
  const totals = useMemo(() => {
    const acc = { IN: 0, OUT: 0, INCount: 0, OUTCount: 0 };
    (allRules ?? []).forEach((r) => {
      if (r.status !== 'ACTIVE') return;
      if (r.direction === 'IN') {
        acc.IN += Number(r.amount);
        acc.INCount += 1;
      } else {
        acc.OUT += Number(r.amount);
        acc.OUTCount += 1;
      }
    });
    return acc;
  }, [allRules]);

  // Linhas filtradas pela aba e ordenadas pela coluna escolhida.
  const tableRows = useMemo(() => {
    const rows = (allRules ?? []).filter((r) => r.direction === tab);
    const factor = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const primary = compareRules(a, b, sort.column) * factor;
      if (primary !== 0) return primary;
      // desempate estável: dia, descrição
      const dayCmp = a.dayOfMonth - b.dayOfMonth;
      if (dayCmp !== 0) return dayCmp;
      return a.description.localeCompare(b.description, 'pt-BR');
    });
  }, [allRules, tab, sort]);

  const tabTotal = useMemo(
    () => tableRows.reduce((s, r) => s + Number(r.amount), 0),
    [tableRows],
  );

  function toggleSort(column: SortColumn) {
    setSort((cur) =>
      cur.column === column
        ? { column, dir: cur.dir === 'asc' ? 'desc' : 'asc' }
        : { column, dir: 'asc' },
    );
  }

  const generateAll = useMutation({
    mutationFn: () => recurringApi.generateNext(),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['payables'] });
      qc.invalidateQueries({ queryKey: ['receivables'] });
      qc.invalidateQueries({ queryKey: ['payables-summary'] });
      qc.invalidateQueries({ queryKey: ['receivables-summary'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      qc.invalidateQueries({ queryKey: ['recurring'] });
      const msg = `${res.created} título(s) gerado(s)` +
        (res.skipped > 0 ? `. ${res.skipped} regra(s) já em dia.` : '.');
      alert(msg);
    },
  });

  const generateOne = useMutation({
    mutationFn: (id: string) => recurringApi.generateOne(id),
    onSuccess: (res, id) => {
      qc.invalidateQueries({ queryKey: ['payables'] });
      qc.invalidateQueries({ queryKey: ['receivables'] });
      qc.invalidateQueries({ queryKey: ['payables-summary'] });
      qc.invalidateQueries({ queryKey: ['receivables-summary'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      qc.invalidateQueries({ queryKey: ['recurring'] });
      if (!res.created) {
        alert(`Nenhum título gerado para esta regra: ${res.reason ?? 'já em dia'}.`);
      } else {
        alert(`Título gerado para ${res.targetMonth}.`);
      }
      void id;
    },
    onError: (err) => {
      alert(err instanceof ApiError ? err.message : 'Erro ao gerar título.');
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => recurringApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recurring'] });
    },
    onError: (err) => {
      alert(err instanceof ApiError ? err.message : 'Erro ao remover regra.');
    },
  });

  function handleDelete(rule: RecurringRule) {
    const ok = window.confirm(
      `Remover a regra "${rule.description}" de ${rule.party.name}?\n\n` +
      `Os títulos já gerados a partir dela não são apagados, apenas perdem o vínculo com a regra.`,
    );
    if (ok) remove.mutate(rule.id);
  }

  return (
    <>
      <PageHeader
        title="Contratos e despesas fixas"
        breadcrumb="Recorrências"
        actions={
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => generateAll.mutate()}
              disabled={generateAll.isPending}
            >
              <CalendarPlus size={14} className="mr-1" />
              {generateAll.isPending ? 'Gerando…' : 'Gerar próximas faturas'}
            </Button>
            <Button size="sm" onClick={() => setFormDialog({ mode: 'create', direction: tab })}>
              <Plus size={14} className="mr-1" /> Nova
            </Button>
          </div>
        }
      />
      <PageBody>
        <TotalsCards totals={totals} />

        <div className="flex gap-4 text-sm border-b">
          <button
            onClick={() => setTab('IN')}
            className={`pb-2 ${tab === 'IN' ? 'border-b-2 border-primary font-medium' : 'text-muted-foreground'}`}
          >
            A receber
          </button>
          <button
            onClick={() => setTab('OUT')}
            className={`pb-2 ${tab === 'OUT' ? 'border-b-2 border-primary font-medium' : 'text-muted-foreground'}`}
          >
            A pagar
          </button>
        </div>

        <div className="bg-card border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-muted-foreground">
              <tr>
                <SortHeader column="party"           label="Contraparte"     sort={sort} onClick={toggleSort} />
                <SortHeader column="description"     label="Serviço"         sort={sort} onClick={toggleSort} />
                <SortHeader column="chart"           label="Categoria"       sort={sort} onClick={toggleSort} />
                <SortHeader column="dayOfMonth"      label="Dia"             sort={sort} onClick={toggleSort} />
                <SortHeader column="amount"          label="Valor"           sort={sort} onClick={toggleSort} align="right" />
                <SortHeader column="status"          label="Status"          sort={sort} onClick={toggleSort} />
                <SortHeader column="lastGeneratedAt" label="Última geração"  sort={sort} onClick={toggleSort} />
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y">
              {isLoading && (
                <tr><td colSpan={8} className="px-3 py-6 text-center text-muted-foreground">Carregando…</td></tr>
              )}
              {tableRows.map((r) => (
                <tr key={r.id}>
                  <td className="px-3 py-2">{r.party.name}</td>
                  <td className="px-3 py-2">{r.description}</td>
                  <td className="px-3 py-2 text-muted-foreground">{r.chartOfAccount.name}</td>
                  <td className="px-3 py-2">{r.dayOfMonth}</td>
                  <td className="px-3 py-2 text-right font-medium">{formatBRL(r.amount)}</td>
                  <td className="px-3 py-2"><Badge variant={statusVariant[r.status]}>{statusLabel[r.status]}</Badge></td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {r.lastGeneratedAt ? new Date(r.lastGeneratedAt).toLocaleDateString('pt-BR') : '—'}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => generateOne.mutate(r.id)}
                        disabled={generateOne.isPending || r.status !== 'ACTIVE'}
                        aria-label="Gerar próxima"
                        title={r.status === 'ACTIVE' ? 'Gerar próxima fatura' : 'Regra inativa'}
                      >
                        <CalendarPlus size={14} />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setFormDialog({ mode: 'edit', rule: r })}
                        aria-label="Editar"
                        title="Editar"
                      >
                        <Pencil size={14} />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(r)}
                        disabled={remove.isPending}
                        aria-label="Remover"
                        title="Remover"
                      >
                        <Trash2 size={14} className="text-rose-600" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {!isLoading && tableRows.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-6 text-center text-muted-foreground">Nenhuma regra recorrente</td></tr>
              )}
            </tbody>
            {tableRows.length > 0 && (
              <tfoot className="bg-muted/30 border-t font-medium">
                <tr>
                  <td className="px-3 py-2 text-muted-foreground" colSpan={4}>
                    Total ({tableRows.length} {tableRows.length === 1 ? 'regra' : 'regras'})
                  </td>
                  <td className="px-3 py-2 text-right">{formatBRL(tabTotal)}</td>
                  <td colSpan={3} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        <div className="text-xs text-muted-foreground">
          Use <span className="font-medium">Gerar próximas faturas</span> para lançar o próximo mês ainda
          não gerado de cada regra ativa, ou o ícone ao lado de cada linha para gerar uma a uma. O job
          diário <code className="bg-muted px-1 rounded">recurring:daily</code> também executa essa rotina automaticamente.
          Clique em qualquer cabeçalho da tabela para ordenar.
        </div>
      </PageBody>

      {formDialog && (
        <RecurringFormDialog
          mode={formDialog.mode}
          direction={formDialog.mode === 'create' ? formDialog.direction : formDialog.rule.direction}
          rule={formDialog.mode === 'edit' ? formDialog.rule : undefined}
          onClose={() => setFormDialog(null)}
        />
      )}
    </>
  );
}

// =====================================================================
// Cards de total (regras ATIVAS, valor mensal, por direção)
// =====================================================================
interface TotalsCardsProps {
  totals: { IN: number; OUT: number; INCount: number; OUTCount: number };
}

function TotalsCards({ totals }: TotalsCardsProps) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <div className="rounded-lg border bg-card border-emerald-200 overflow-hidden">
        <div className="px-3 py-1.5 text-sm font-medium text-center bg-emerald-600 text-white">
          A receber recorrente (mensal)
        </div>
        <div className="px-3 py-3 text-center">
          <div className="text-xl font-medium text-emerald-700">{formatBRL(totals.IN)}</div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            {totals.INCount} {totals.INCount === 1 ? 'regra ativa' : 'regras ativas'}
          </div>
        </div>
      </div>
      <div className="rounded-lg border bg-card border-rose-200 overflow-hidden">
        <div className="px-3 py-1.5 text-sm font-medium text-center bg-rose-600 text-white">
          A pagar recorrente (mensal)
        </div>
        <div className="px-3 py-3 text-center">
          <div className="text-xl font-medium text-rose-700">{formatBRL(totals.OUT)}</div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            {totals.OUTCount} {totals.OUTCount === 1 ? 'regra ativa' : 'regras ativas'}
          </div>
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// Cabeçalho ordenável
// =====================================================================
interface SortHeaderProps {
  column: SortColumn;
  label: string;
  sort: SortState;
  onClick: (col: SortColumn) => void;
  align?: 'left' | 'right';
}

function SortHeader({ column, label, sort, onClick, align = 'left' }: SortHeaderProps) {
  const isActive = sort.column === column;
  const Icon = !isActive ? ArrowUpDown : sort.dir === 'asc' ? ArrowUp : ArrowDown;
  const justify = align === 'right' ? 'justify-end' : 'justify-start';
  return (
    <th className="px-3 py-2 font-medium select-none">
      <button
        type="button"
        onClick={() => onClick(column)}
        className={`inline-flex items-center gap-1 ${justify} w-full text-left hover:text-foreground transition-colors ${isActive ? 'text-foreground' : ''}`}
        aria-sort={isActive ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      >
        <span>{label}</span>
        <Icon size={12} className={isActive ? 'opacity-100' : 'opacity-40'} />
      </button>
    </th>
  );
}

// =====================================================================
// Form: Nova regra ou edição
// =====================================================================
interface RecurringFormDialogProps {
  mode: 'create' | 'edit';
  direction: Direction;
  rule?: RecurringRule;
  onClose: () => void;
}

function RecurringFormDialog({ mode, direction, rule, onClose }: RecurringFormDialogProps) {
  const qc = useQueryClient();
  const isEdit = mode === 'edit';
  const today = new Date().toISOString().slice(0, 10);

  const [partyId, setPartyId] = useState(rule?.party.id ?? '');
  const [chartOfAccountId, setChartOfAccountId] = useState(rule?.chartOfAccount.id ?? '');
  const [costCenterId, setCostCenterId] = useState(rule?.costCenter?.id ?? '');
  const [description, setDescription] = useState(rule?.description ?? '');
  const [amount, setAmount] = useState(rule ? Number(rule.amount).toFixed(2) : '');
  const [dayOfMonth, setDayOfMonth] = useState(rule ? String(rule.dayOfMonth) : '5');
  const [startDate, setStartDate] = useState(rule ? rule.startDate.slice(0, 10) : today);
  const [endDate, setEndDate] = useState(rule?.endDate ? rule.endDate.slice(0, 10) : '');
  const [status, setStatus] = useState<RecurringStatus>(rule?.status ?? 'ACTIVE');

  const { data: parties } = useQuery({ queryKey: ['parties'], queryFn: () => partiesApi.list() });
  const { data: charts } = useQuery({ queryKey: ['charts'], queryFn: chartApi.list });
  const { data: ccs } = useQuery({ queryKey: ['ccs'], queryFn: costCentersApi.list });

  const filteredParties = useMemo(() => {
    if (!parties) return [];
    const wanted = direction === 'IN'
      ? (p: { type: string; active: boolean }) => p.type === 'CUSTOMER' && p.active
      : (p: { type: string; active: boolean }) => (p.type === 'SUPPLIER' || p.type === 'EMPLOYEE' || p.type === 'CONTRACTOR') && p.active;
    return parties.filter(wanted);
  }, [parties, direction]);

  const filteredCharts = useMemo(() => {
    if (!charts) return [];
    const nature = direction === 'IN' ? 'REVENUE' : 'EXPENSE';
    return charts.filter((c) => c.nature === nature && c.active);
  }, [charts, direction]);

  const create = useMutation({
    mutationFn: () => recurringApi.create({
      partyId,
      chartOfAccountId,
      ...(costCenterId ? { costCenterId } : {}),
      description,
      direction,
      amount: Number(amount),
      dayOfMonth: Number(dayOfMonth),
      startDate,
      ...(endDate ? { endDate } : {}),
      status,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recurring'] });
      onClose();
    },
  });

  const update = useMutation({
    mutationFn: () => {
      if (!rule) throw new Error('rule ausente em modo edit');
      const body: Record<string, unknown> = {};
      if (partyId !== rule.party.id) body.partyId = partyId;
      if (chartOfAccountId !== rule.chartOfAccount.id) body.chartOfAccountId = chartOfAccountId;
      if ((costCenterId || '') !== (rule.costCenter?.id ?? '')) {
        body.costCenterId = costCenterId || null;
      }
      if (description !== rule.description) body.description = description;
      if (Number(amount).toFixed(2) !== Number(rule.amount).toFixed(2)) body.amount = Number(amount);
      if (Number(dayOfMonth) !== rule.dayOfMonth) body.dayOfMonth = Number(dayOfMonth);
      if (startDate !== rule.startDate.slice(0, 10)) body.startDate = startDate;
      const currentEnd = rule.endDate ? rule.endDate.slice(0, 10) : '';
      if (endDate !== currentEnd) body.endDate = endDate || null;
      if (status !== rule.status) body.status = status;
      return recurringApi.update(rule.id, body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recurring'] });
      onClose();
    },
  });

  const mut = isEdit ? update : create;
  const apiError = mut.error instanceof ApiError ? mut.error.message : null;

  const dayNum = Number(dayOfMonth);
  const requiredOk =
    partyId &&
    chartOfAccountId &&
    description.trim().length > 0 &&
    amount &&
    Number(amount) > 0 &&
    dayNum >= 1 && dayNum <= 31 &&
    startDate;

  const partyLabel = direction === 'IN' ? 'Cliente' : 'Fornecedor';
  const chartLabel = direction === 'IN' ? 'Categoria (receita)' : 'Categoria (despesa)';
  const titleBase = direction === 'IN' ? 'A receber' : 'A pagar';

  return (
    <Dialog
      open
      onClose={onClose}
      title={isEdit ? `Editar regra recorrente — ${titleBase}` : `Nova regra recorrente — ${titleBase}`}
      className="max-w-2xl"
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!requiredOk) return;
          mut.mutate();
        }}
      >
        <DialogBody className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>{partyLabel}</Label>
              <Select value={partyId} onChange={(e) => setPartyId(e.target.value)}>
                <option value="">— selecione —</option>
                {filteredParties.map((p) => (
                  <option key={p.id} value={p.id}>{p.code} — {p.name}</option>
                ))}
                {isEdit && rule && !filteredParties.some((p) => p.id === rule.party.id) && (
                  <option value={rule.party.id}>{rule.party.code} — {rule.party.name} (inativo)</option>
                )}
              </Select>
            </div>

            <div className="space-y-1">
              <Label>{chartLabel}</Label>
              <Select value={chartOfAccountId} onChange={(e) => setChartOfAccountId(e.target.value)}>
                <option value="">— selecione —</option>
                {filteredCharts.map((c) => (
                  <option key={c.id} value={c.id}>{c.code} — {c.name}</option>
                ))}
                {isEdit && rule && !filteredCharts.some((c) => c.id === rule.chartOfAccount.id) && (
                  <option value={rule.chartOfAccount.id}>
                    {rule.chartOfAccount.code} — {rule.chartOfAccount.name} (inativa)
                  </option>
                )}
              </Select>
            </div>
          </div>

          <div className="space-y-1">
            <Label>Descrição / serviço</Label>
            <Input
              placeholder={direction === 'IN' ? 'Ex.: Mensalidade contrato XPTO' : 'Ex.: Aluguel sala — janeiro'}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={500}
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label>Valor mensal</Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                placeholder="0,00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label>Dia do mês</Label>
              <Input
                type="number"
                min="1"
                max="31"
                value={dayOfMonth}
                onChange={(e) => setDayOfMonth(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label>Status</Label>
              <Select value={status} onChange={(e) => setStatus(e.target.value as RecurringStatus)}>
                <option value="ACTIVE">Ativo</option>
                <option value="WAITING">Aguardando</option>
                <option value="PAUSED">Pausado</option>
                <option value="CANCELED">Cancelado</option>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label>Início</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>Fim (opcional)</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>Centro de custo</Label>
              <Select value={costCenterId} onChange={(e) => setCostCenterId(e.target.value)}>
                <option value="">—</option>
                {ccs?.map((cc) => <option key={cc.id} value={cc.id}>{cc.name}</option>)}
              </Select>
            </div>
          </div>

          {amount && Number(amount) > 0 && (
            <div className="text-xs text-muted-foreground bg-muted/40 rounded-md p-2">
              {direction === 'IN' ? 'Receita' : 'Despesa'} mensal de {formatBRL(amount)} — vencimento todo dia {dayOfMonth || '?'}.
            </div>
          )}

          {apiError && (
            <div className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md p-2">
              {apiError}
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
          <Button type="submit" disabled={!requiredOk || mut.isPending}>
            {mut.isPending ? 'Salvando…' : isEdit ? 'Salvar alterações' : 'Criar regra'}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
